import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Modal, // Import Modal
  Paper, // Import Paper for Modal styling
} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";

// Basic Modal Style (can be moved to a separate CSS/JSS file)
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const CustomSessionQuestionItem = ({
  question, // The question object from the generated list
  index,
  currentUser,
  expanded,
  toggleExpanded,
  onAnswerSubmit, // Function to handle user's answer submission
  isLoading, // Add isLoading prop
  isFake, // Add isFake prop for showcase questions
}) => {
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // State for feedback (Correct/Incorrect)
  const [answerRevealed, setAnswerRevealed] = useState(false); // State to track if the answer is revealed
  const [showPremiumModal, setShowPremiumModal] = useState(false); // State for premium modal

  // Adapt difficulty label mapping if needed, or use the difficulty string directly from the question object
  const difficultyLabelMap = {
    Entry: "débutant",
    Junior: "junior",
    Middle: "expérimenté",
    Senior: "senior",
    Expert: "expert",
  };

  const difficultyLabel =
    difficultyLabelMap[question.difficulty] || question.difficulty; // Use provided difficulty or map it

  const handleAnswerInputChange = (event) => {
    setUserAnswer(event.target.value);
  };

  const handlePremiumModalOpen = () => {
    setShowPremiumModal(true);
  };

  const handlePremiumModalClose = () => {
    setShowPremiumModal(false);
  };

  const handleSubmitAnswer = () => {
    if (!currentUser?.isPremium) {
      // If user is not premium, show the modal instead of submitting
      handlePremiumModalOpen();
      return;
    }

    // If user is premium, proceed with submission and comparison
    onAnswerSubmit(question.id, userAnswer);

    // Implement actual comparison and feedback logic
    const correctAnswer = question.collapsedAnswer;
    // Basic case-insensitive comparison
    // Ensure correctAnswer exists before comparing
    if (
      // TODO : Comparison based on API LLM
      correctAnswer &&
      userAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase()
    ) {
      setFeedback("Correct!");
    } else {
      setFeedback("Incorrect.");
    }

    // Reveal the answer after submission
    setAnswerRevealed(true);
  };

  // Determine if the answer should be shown (expanded AND (premium OR answer revealed after test mode))
  const showAnswer = expanded && (currentUser?.isPremium || answerRevealed);

  return (
    <Card
      style={{ marginBottom: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
    >
      <CardContent style={{ padding: "16px" }}>
        <Grid
          container
          justify="space-between"
          alignItems="center"
          // Disable click and change cursor if it's a fake question
          style={{ cursor: isFake ? "default" : "pointer" }}
          onClick={isFake ? undefined : () => toggleExpanded(question.id)}
        >
          <Typography
            variant="h6"
            // Apply blur style if fake
            style={isFake ? { filter: "blur(4px)", userSelect: "none" } : {}}
          >
            <span
              style={{
                fontStyle: "italic",
                color: "#6c757d",
                fontWeight: "bolder",
                marginRight: "1rem",
              }}
            >
              Q{index + 1}:
            </span>
            {question.libelle} {/* Corrected: Use libelle */}
          </Typography>
          <Box style={{ display: "flex", alignItems: "center" }}>
            {/* Always show lock for fake questions */}
            {/* OR Show lock only if answer shouldn't be shown and user isn't premium */}
            {(isFake ||
              (!currentUser?.isPremium && !showAnswer && expanded)) && (
              <LockIcon
                style={{
                  marginRight: "8px",
                  color: isFake ? "#bdbdbd" : "inherit",
                }}
              />
            )}
            <Typography
              variant="caption"
              style={{
                // Dim color if fake
                backgroundColor: isFake
                  ? "#e0e0e0"
                  : getDifficultyColor(question.difficulty),
                color: isFake ? "#757575" : "#ffffff",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              {difficultyLabel}
            </Typography>
          </Box>
        </Grid>

        {/* Only render content below if NOT fake */}
        {expanded && !isFake && (
          <Box style={{ marginTop: "16px" }}>
            {/* Display question explanation if available */}
            {/* Show explanation only if user is premium or answer is revealed */}
            {question.explanation && showAnswer && (
              <Box style={{ marginBottom: "16px" }}>
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  Explication:
                </Typography>
                <Typography variant="body2">{question.explanation}</Typography>
              </Box>
            )}

            {/* Test Mode Input */}
            <Box style={{ marginBottom: "16px" }}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                Votre réponse:
              </Typography>
              <TextField
                value={userAnswer}
                onChange={handleAnswerInputChange}
                placeholder="Entrer votre réponse ici..."
                multiline
                rows={4}
                variant="outlined"
                fullWidth
                // Textarea is always enabled unless main loading is happening
                disabled={isLoading}
              />
              <Button
                onClick={handleSubmitAnswer} // Click always handled, logic inside checks premium
                variant="contained"
                color="primary"
                disabled={isLoading} // Only disable during main loading
                style={{ marginTop: "8px" }}
              >
                Soumettre
              </Button>
              {/* Show feedback only if user is premium (since only they can submit) */}
              {feedback && currentUser?.isPremium && (
                <Typography
                  variant="body2"
                  style={{
                    marginTop: "8px",
                    color: feedback === "Correct!" ? "#4caf50" : "#f44336",
                  }}
                >
                  {feedback}
                </Typography>
              )}
            </Box>

            {/* Display Correct Answer */}
            {/* Show answer only if user is premium or answer is revealed */}
            {showAnswer && question.collapsedAnswer && (
              <Box style={{ marginBottom: "16px" }}>
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  Réponse correcte:
                </Typography>
                <Typography variant="body2">
                  {question.collapsedAnswer}
                </Typography>
              </Box>
            )}

            {/* Message if answer/explanation is locked */}
            {!currentUser?.isPremium && expanded && !showAnswer && (
              <Box
                style={{
                  textAlign: "center",
                  padding: "16px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" style={{ color: "#757575" }}>
                  Devenez membre premium pour voir la réponse et l'explication,
                  ou tester votre propre réponse.
                </Typography>
                {/* Placeholder for upgrade button/link */}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Premium Required Modal */}
      <Modal
        open={showPremiumModal}
        onClose={handlePremiumModalClose}
        aria-labelledby="premium-modal-title"
        aria-describedby="premium-modal-description"
      >
        <Paper sx={modalStyle} style={modalStyle}>
          {" "}
          {/* Apply basic style */}
          <Typography id="premium-modal-title" variant="h6" component="h2">
            Fonctionnalité Premium{" "}
          </Typography>
          <Typography id="premium-modal-description" sx={{ mt: 2 }}>
            Tester votre réponse et obtenir des commentaires détaillés est une
            fonctionnalité premium. Mettez votre compte à niveau pour bénéficier
            de cet avantage !
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <img
                src="/assets/animation-premium-custom-session.gif"
                alt="Premium feature animation"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </Box>
          </Typography>
          <Button
            onClick={handlePremiumModalClose}
            style={{ marginTop: "16px" }}
          >
            Fermer
          </Button>
        </Paper>
      </Modal>
    </Card>
  );
};

// Helper function to assign colors based on difficulty
const getDifficultyColor = (difficulty) => {
  // Handle potential undefined difficulty
  const lowerDifficulty = difficulty?.toLowerCase() || "";
  switch (lowerDifficulty) {
    case "entry":
      return "#4caf50"; // Green
    case "junior":
      return "#8bc34a"; // Light Green
    case "middle":
      return "#ff9800"; // Orange
    case "senior":
      return "#f44336"; // Red
    case "expert":
      return "#d32f2f"; // Dark Red
    default:
      return "#9e9e9e"; // Grey
  }
};

export default CustomSessionQuestionItem;
